import {ModelCondition} from '@/engine';
import {DependencyRegistrer} from '@/engine/types/dependencies';
import {getDependenciesFromArrayFilters, getValueDependencies} from '@/engine/utils/dependencies';
import {Condition, Reference} from '@/model/types';
import {DOMAIN_VALUE_PREFIX} from '@/model/types/constants';
import {getPredicate} from '../predicates';

export const createConditionFactory = (
  condition: Condition,
  registerDependency?: DependencyRegistrer,
  dependencyCategory: 'all' | 'visibility' = 'all',
): ModelCondition => {
  const predicate = getPredicate(condition);

  // Register the dependencies for the field property
  registerDependency?.(dependencyCategory, ...getConditionDependencies(condition));

  // Register the dependencies for the value property
  if ('value' in condition) {
    if (Array.isArray(condition.value)) {
      condition.value.map(value => {
        if (typeof value == 'object') registerDependency?.(dependencyCategory, ...getValueDependencies(value));
      });
    } else {
      if (typeof condition.value == 'object') {
        registerDependency?.(dependencyCategory, ...getValueDependencies(condition.value));
      }
    }
  }

  return predicate;
};

const isValueReference = (value: string | number | Reference): value is Reference => {
  return typeof value === 'object' && '$ref' in value && !value.$ref.startsWith(DOMAIN_VALUE_PREFIX);
};

export const getConditionDependencies = (condition: Condition): string[] => {
  switch (condition.operator) {
    case '!=':
    case '=':
    case '>':
    case '>=':
    case '<':
    case 'has':
    case '<=': {
      const valueRef = isValueReference(condition.value) ? [condition.value.$ref] : [];

      if (typeof condition.field === 'string') {
        return [condition.field, ...valueRef];
      } else if ('$ref' in condition.field) {
        return [condition.field.$ref, ...valueRef];
      } else if ('$template' in condition.field) {
        return getValueDependencies(condition.field.$template);
      } else if ('$query' in condition.field) {
        return [condition.field.$query, ...valueRef];
      } else if ('$first' in condition.field.$generated) {
        const {array, field, filters = []} = condition.field.$generated.$first;
        return [`_${array}[count]`, field.asQuery(), ...getDependenciesFromArrayFilters(filters)];
      } else if ('fields' in condition.field.$generated.$sum) {
        return condition.field.$generated.$sum.fields;
      } else {
        return condition.field.$generated.$sum.array.count.flatMap(arrayId => {
          if (typeof arrayId === 'string') {
            return `_${arrayId}[count]`;
          } else {
            const filterDependencies = getDependenciesFromArrayFilters(arrayId.filters ?? []);
            if ('groupBy' in arrayId && arrayId.groupBy) {
              return [`${arrayId.groupBy}#*`, `_${arrayId.array}[count]`, ...filterDependencies];
            }
            return [`_${arrayId.array}[count]`, ...filterDependencies];
          }
        });
      }
    }
    case 'in':
    case 'not in': {
      const valueRefs = condition.value
        .filter((value): value is Reference => typeof value !== 'string' && !value.$ref.startsWith(DOMAIN_VALUE_PREFIX))
        .map(value => value.$ref);
      return [condition.field, ...valueRefs];
    }
    case 'and':
    case 'or':
    case 'not':
      return condition.conditions.flatMap(getConditionDependencies);
    case 'empty':
    case 'not empty':
      return condition.fields;
    case 'visible':
      return [condition.field];
    case 'array equal':
      return condition.arrays.flatMap(({array, filters = []}, side) => [
        `_${array}[count]`,
        ...condition.fields.map(fieldPair => fieldPair[side].asQuery()),
        ...getDependenciesFromArrayFilters(filters),
      ]);
  }
};
